import React from 'react';
import { useLocation } from 'react-router-dom';
import { 
  FaWhatsapp, FaMapMarkerAlt, FaClock, FaGlobe, 
  FaEnvelope, FaPhone, FaFacebook, FaInstagram, 
  FaTwitter 
} from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  // Function to handle smooth scroll to sections on home page
  const handleScrollToSection = (e, sectionId) => {
    e.preventDefault();
    
    // If we're not on the home page, navigate to home page first with the section hash
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    // Scroll to section on home page
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Navbar height
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  // Function to handle tour type filtering (stays on home page)
  const handleTourFilter = (e, filterType) => {
    e.preventDefault();
    
    // First scroll to tours section
    if (location.pathname !== '/') {
      window.location.href = `/#tours?type=${filterType}`;
      return;
    }
    
    // Scroll to tours section
    const toursSection = document.getElementById('tours');
    if (toursSection) {
      const offset = 80;
      const elementPosition = toursSection.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      // You can add filter logic here if needed
      console.log(`Filter by: ${filterType}`);
    }
  };

  return (
    <footer className="bg-[#0B2B2B] text-white"> {/* Dark green-black background */}
      {/* ===== MAIN FOOTER - 4 COLUMNS ===== */}
      <div className="py-16 md:py-20 bg-[#0B2B2B]">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            
            {/* COLUMN 1: Logo & About */}
            <div>
              {/* Logo Image */}
              <div className="mb-4">
                <img 
                  src="/images/encourage-women-logo.png" 
                  alt="Encourage Women Ethiopia"
                  className="h-20 w-auto mb-4"
                />
              </div>
              
              {/* Updated company description */}
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                Encourage Women Ethiopia Tour and Travel
              </p>
              
              {/* GOLD "10% of profits" line */}
              <p className="text-[#FFD700] text-xs leading-relaxed mb-4">
                10% of our profits support women's associations<br />
                and community-based projects in Ethiopia.
              </p>
              
              {/* Social Media Icons - Added here as requested */}
              <div className="flex gap-4 mt-4">
                <a 
                  href="https://facebook.com/encouragewomenethiopia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#FFD700] transition-colors text-xl"
                  aria-label="Facebook"
                >
                  <FaFacebook />
                </a>
                <a 
                  href="https://instagram.com/encouragewomenethiopia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#FFD700] transition-colors text-xl"
                  aria-label="Instagram"
                >
                  <FaInstagram />
                </a>
                <a 
                  href="https://twitter.com/encouragewomen" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#FFD700] transition-colors text-xl"
                  aria-label="Twitter"
                >
                  <FaTwitter />
                </a>
              </div>
            </div>

            {/* COLUMN 2: Quick Links - ALL STAY ON HOME PAGE */}
            <div>
              <h5 className="font-semibold text-lg mb-4 text-[#FFD700]">Quick Links</h5>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="/#home"
                    onClick={(e) => handleScrollToSection(e, 'home')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Home
                  </a>
                </li>
                <li>
                  <a 
                    href="/#about"
                    onClick={(e) => handleScrollToSection(e, 'about')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleScrollToSection(e, 'tours')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Tour Packages
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'city')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    City Tours
                  </a>
                </li>
                <li>
                  <a 
                    href="/#reviews"
                    onClick={(e) => {
                      e.preventDefault();
                      // If reviews section exists, scroll to it
                      handleScrollToSection(e, 'reviews');
                    }}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Reviews
                  </a>
                </li>
                <li>
                  <a 
                    href="/#gallery"
                    onClick={(e) => {
                      e.preventDefault();
                      // If gallery section exists, scroll to it
                      handleScrollToSection(e, 'gallery');
                    }}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Gallery
                  </a>
                </li>
                <li>
                  <a 
                    href="/#contact"
                    onClick={(e) => handleScrollToSection(e, 'contact')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* COLUMN 3: Our Tours - ALL STAY ON HOME PAGE */}
            <div>
              <h5 className="font-semibold text-lg mb-4 text-[#FFD700]">Our Tours</h5>
              <ul className="space-y-2">
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'cultural')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Cultural & Historical Tours
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'women-only')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Women-Only Group Tours
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'nature')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Nature & Adventure Tours
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'coffee')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Coffee & Culinary Experiences
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'custom')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    Custom & Private Tours
                  </a>
                </li>
                <li>
                  <a 
                    href="/#tours"
                    onClick={(e) => handleTourFilter(e, 'city')}
                    className="text-gray-300 hover:text-[#FFD700] transition-colors text-sm cursor-pointer"
                  >
                    City Tours
                  </a>
                </li>
              </ul>
            </div>

            {/* COLUMN 4: Contact Us */}
            <div>
              <h5 className="font-semibold text-lg mb-4 text-[#FFD700]">Contact Us</h5>
              <div className="space-y-3">
                {/* Address */}
                <div className="flex items-start gap-3 text-gray-300">
                  <FaMapMarkerAlt className="mt-1 flex-shrink-0 text-[#FFD700]" />
                  <p className="text-sm">
                    Bole Sub City, Addis Ababa<br />
                    Ethiopia
                  </p>
                </div>
                
                {/* Hours */}
                <div className="flex items-start gap-3 text-gray-300">
                  <FaClock className="mt-1 flex-shrink-0 text-[#FFD700]" />
                  <p className="text-sm">
                    Mon–Sun: 9:00 AM–6:00 PM (EAT)
                  </p>
                </div>
                
                {/* Online Support */}
                <div className="flex items-start gap-3 text-gray-300">
                  <FaGlobe className="mt-1 flex-shrink-0 text-[#FFD700]" />
                  <p className="text-sm">
                    International: 24/7 Online Support
                  </p>
                </div>
                
                {/* Phone */}
                <div className="flex items-start gap-3 text-gray-300">
                  <FaPhone className="mt-1 flex-shrink-0 text-[#FFD700]" />
                  <p className="text-sm">
                    <a href="tel:+251912093264" className="hover:text-[#FFD700] transition-colors">
                      +251 912 093 264
                    </a>
                  </p>
                </div>
                
                {/* Email */}
                <div className="flex items-start gap-3 text-gray-300">
                  <FaEnvelope className="mt-1 flex-shrink-0 text-[#FFD700]" />
                  <div>
                    <p className="text-sm">
                      <a href="mailto:info@encouragewomen.com" className="hover:text-[#FFD700] transition-colors">
                        info@encouragewomen.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
              
              {/* WhatsApp Button */}
              <div className="mt-6">
                <a 
                  href="https://wa.me/251912093264"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5C] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"
                >
                  <FaWhatsapp size={18} /> Chat on WhatsApp
                </a>
              </div>
              
              {/* "Also find us on" with Viator, GetYourGuide, TripAdvisor */}
              <div className="mt-4">
                <p className="text-gray-300 text-sm mb-2">Also find us on:</p>
                <div className="flex flex-wrap items-center gap-2 text-[#FFD700] text-sm">
                  <a href="https://viator.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Viator
                  </a>
                  <span className="text-gray-500">•</span>
                  <a href="https://getyourguide.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    GetYourGuide
                  </a>
                  <span className="text-gray-500">•</span>
                  <a href="https://tripadvisor.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    TripAdvisor
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== COPYRIGHT SECTION ===== */}
      <div className="py-6 bg-[#0A2525]">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center text-gray-400 text-sm">
            <p>© {currentYear} Encourage Women Ethiopia Tour and Travel. All rights reserved.</p>
            <p className="mt-1 text-xs text-gray-500">
              Founded by Atsedemariam Mamo | "Travel Bold. Travel Together."
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;